import {ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {Chart, ChartTypeRegistry, TooltipItem} from "chart.js/auto";
import {Subscription} from "rxjs";
import {TimeseriesBarChartStore} from "../../../store/timeseries-bar-chart/timeseries-bar-chart-store";
import {TimeSeriesPerformanceStoreService} from "../../../store/time-series-performance-store.service";
import {ChartEvent} from "chart.js";
import {ErrorCodes} from "../../../../common/enums/states/error-codes";
import {ErrorMessageComponent} from "../../../../common/components/error-message/error-message.component";
import {CommonModule} from "@angular/common";

@Component({
  selector: 'analytics-revenue-time-series-chart',
  templateUrl: './revenue-time-series-chart.component.html',
  imports: [
    CommonModule,
    ErrorMessageComponent
  ],
  styleUrls: ['./revenue-time-series-chart.component.css']
})
export class RevenueTimeSeriesChartComponent implements OnDestroy {
  chart?: Chart;
  subscription: Subscription = new Subscription();
  protected readonly ErrorAnalysis = ErrorCodes;
  protected readonly undefined = undefined;
  private chartElement?: ElementRef<HTMLCanvasElement>;

  constructor(private cd: ChangeDetectorRef,
              private timeSeriesBarChartStore: TimeseriesBarChartStore,
              public timeSeriesPerformanceStore: TimeSeriesPerformanceStoreService
  ) {
    this.subscribe()
  }

  @ViewChild('multiEventRevenueBarChart', {static: false}) set content(content: ElementRef<HTMLCanvasElement>) {
    this.chartElement = content;
    this.createChart();
  }

  subscribe() {
    this.subscription.add(this.timeSeriesBarChartStore.updateNotifier.subscribe(
      () => {
        this.createChart();
      }
    ))
  }

  translateTimestampLabelsToDate(labels: string[]) {
    for (let i = 0; i < labels.length; i++) {
      let date = new Date(parseInt(labels[i]));
      labels[i] = date.toLocaleDateString('default', {month: 'long'}) + " " + date.getFullYear();
    }
  }

  createChart() {
    if (this.chart !== undefined) {
      this.chart?.destroy()
    }
    if (this.timeSeriesBarChartStore.getAbsoluteBarChartDataset() === undefined) return;

    if (this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length === 0 || this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets[0].data.length === 0) {
      return
    }

    if (this.chartElement === undefined) return;


    this.chart = new Chart(this.chartElement?.nativeElement, {
      type: 'bar',
      data: this.timeSeriesBarChartStore.getAbsoluteBarChartDataset(),
      options: {
        maintainAspectRatio: false,
        responsive: true,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            stacked: this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length > 1,
          },
          y: {
            stacked: this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length > 1,
            beginAtZero: true,
            ticks: {
              callback: function (value: any, index: any, values: any) {
                return Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(value);
              }
            }
            //offset: true,
          },


        },
        plugins: {
          legend: {
            display: this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length > 1,
            onHover(e: ChartEvent, legendItem: any, legend: any) {
              if (legendItem === undefined) {
                legend.chart.canvas.style.cursor = 'default';
                return;
              }
              legend.chart.canvas.style.cursor = 'pointer';
            }
          },


          tooltip: {
            callbacks: {
              label: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>) => this.getLabel(tooltipItem),

              footer: (tooltipItems: TooltipItem<"bar" | "line" | "scatter" | "bubble" | "pie" | "doughnut" | "polarArea" | "radar">[]) => this.getFooter(tooltipItems)
            }
          }

        }
      }
    })
  }

  getLabel(tooltipItem: TooltipItem<keyof ChartTypeRegistry>) {
    return this.getRevenueLabel(tooltipItem);
    // TOdo make single method call
  }

  getFooter(tooltipItem: TooltipItem<"bar" | "line" | "scatter" | "bubble" | "pie" | "doughnut" | "polarArea" | "radar">[]) {
    return this.getAccumulatedRevenue(tooltipItem);
    // Todo make single method call
  }

  getRevenueLabel(tooltipItem: TooltipItem<keyof ChartTypeRegistry>) {
    let label = ""
    if (this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length <= 1) {
      label = tooltipItem.label || '';
    } else {
      label = tooltipItem.dataset.label || '';
    }
    return label + ": " + new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(tooltipItem.parsed.y);
  }

  getAccumulatedRevenue(tooltipItems: TooltipItem<"bar" | "line" | "scatter" | "bubble" | "pie" | "doughnut" | "polarArea" | "radar">[]) {

    if (this.timeSeriesBarChartStore.getAbsoluteBarChartDataset().datasets.length <= 1) return "";
    let sum = 0;
    let chart = tooltipItems[0].chart;
    // @ts-ignore
    for (let i = 0; i < chart._metasets.length; i++) {
      // @ts-ignore
      if (!chart._metasets[i].visible) {
        continue;
      }
      // @ts-ignore
      sum += tooltipItems[0]!.parsed._stacks.y[i]
    }
    return "Gesamt: " + new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(sum);
  }

  ngOnDestroy() {
    this.chart?.destroy();
    this.subscription.unsubscribe();
  }
}
